import Phaser from 'phaser';
import { DataRegistry } from '../systems/DataRegistry';
import type { BattleSide, BattlefieldModifier, BattlePoint } from '../types/battle';

interface BattlefieldZone {
  id: string;
  rect: Phaser.Geom.Rectangle;
  color: number;
  alpha: number;
  modifier: Partial<BattlefieldModifier>;
  label?: string;
}

interface ObjectivePoint extends BattlePoint {
  id: string;
  radius: number;
  label: string;
}

const TERRAIN_ALIASES: Record<string, string> = {
  plains: 'plains',
  grassland: 'plains',
  rice: 'rice_fields',
  farmland: 'rice_fields',
  jungle: 'jungle',
  forest: 'jungle',
  highland: 'highlands',
  hills: 'highlands',
  mountain: 'highlands',
  volcanic: 'volcanic',
  river: 'river_crossing',
  coast: 'coast',
  coastal: 'coast',
  town: 'town',
  urban: 'town',
};

const PALETTES: Record<string, { base: number; border: number; accent: number }> = {
  plains: { base: 0x557044, border: 0x9fba78, accent: 0x73945d },
  rice_fields: { base: 0x607c49, border: 0xa8c77d, accent: 0x8ba85d },
  jungle: { base: 0x314e35, border: 0x6f9569, accent: 0x476b45 },
  highlands: { base: 0x675c43, border: 0xa89067, accent: 0x887755 },
  volcanic: { base: 0x51463f, border: 0xa66d50, accent: 0x6c5144 },
  river_crossing: { base: 0x536c45, border: 0x91ae75, accent: 0x3e7585 },
  coast: { base: 0x536c45, border: 0x8eae76, accent: 0x3f7d91 },
  town: { base: 0x655b47, border: 0xaa966d, accent: 0x806e51 },
};

export class Battlefield {
  readonly terrainId: string;
  readonly terrainName: string;
  readonly zones: BattlefieldZone[] = [];
  readonly objectives: ObjectivePoint[] = [];
  readonly bounds: Phaser.Geom.Rectangle;
  readonly attackerDeployment: Phaser.Geom.Rectangle;
  readonly defenderDeployment: Phaser.Geom.Rectangle;
  private readonly baseModifier: BattlefieldModifier;
  private readonly palette: { base: number; border: number; accent: number };

  constructor(readonly provinceId: string, width: number, height: number) {
    const province = DataRegistry.instance.provinces[provinceId];
    const rawTerrain = String(province?.terrain ?? 'plains').toLowerCase();
    this.terrainId = TERRAIN_ALIASES[rawTerrain] ?? (DataRegistry.instance.battleTerrain[rawTerrain] ? rawTerrain : 'plains');
    const terrain = DataRegistry.instance.battleTerrain[this.terrainId] ?? DataRegistry.instance.battleTerrain.plains;
    this.terrainName = terrain?.name ?? 'Plains';
    this.baseModifier = {
      name: this.terrainName,
      move: terrain?.moveModifier ?? 1,
      defense: terrain?.defenseModifier ?? 1,
      ranged: terrain?.rangedModifier ?? 1,
      cover: terrain?.cover ?? 0,
      elevation: terrain?.elevation ?? 0,
      fatigue: terrain?.fatigueModifier ?? 1,
    };
    this.palette = PALETTES[this.terrainId] ?? PALETTES.plains;
    this.bounds = new Phaser.Geom.Rectangle(58, 126, width - 116, height - 244);
    this.attackerDeployment = new Phaser.Geom.Rectangle(this.bounds.x + 24, this.bounds.y + 34, this.bounds.width * 0.28, this.bounds.height - 68);
    this.defenderDeployment = new Phaser.Geom.Rectangle(this.bounds.right - this.bounds.width * 0.28 - 24, this.bounds.y + 34, this.bounds.width * 0.28, this.bounds.height - 68);
    this.buildZones();
    this.objectives.push({ id: 'center', x: width / 2, y: height / 2 - 18, radius: 72, label: 'Field Objective' });
    if (this.terrainId === 'river_crossing') this.objectives.push({ id: 'ford', x: width / 2, y: height / 2 + 154, radius: 56, label: 'Southern Ford' });
    if (this.terrainId === 'town') this.objectives.push({ id: 'market', x: width / 2 + 145, y: height / 2 - 120, radius: 52, label: 'Market Square' });
  }

  deploymentZone(side: BattleSide) {
    return side === 'attacker' ? this.attackerDeployment : this.defenderDeployment;
  }

  clampToBattle(point: BattlePoint): BattlePoint {
    return {
      x: Phaser.Math.Clamp(point.x, this.bounds.x + 20, this.bounds.right - 20),
      y: Phaser.Math.Clamp(point.y, this.bounds.y + 20, this.bounds.bottom - 20),
    };
  }

  insideDeployment(side: BattleSide, point: BattlePoint) {
    return this.deploymentZone(side).contains(point.x, point.y);
  }

  modifierAt(x: number, y: number): BattlefieldModifier {
    const result = { ...this.baseModifier };
    for (const zone of this.zones) {
      if (!zone.rect.contains(x, y)) continue;
      result.name = zone.label ?? result.name;
      result.move *= zone.modifier.move ?? 1;
      result.defense *= zone.modifier.defense ?? 1;
      result.ranged *= zone.modifier.ranged ?? 1;
      result.cover = Phaser.Math.Clamp(result.cover + (zone.modifier.cover ?? 0), 0, 0.85);
      result.elevation += zone.modifier.elevation ?? 0;
      result.fatigue *= zone.modifier.fatigue ?? 1;
    }
    return result;
  }

  objectiveAt(x: number, y: number) {
    return this.objectives.find((objective) => Phaser.Math.Distance.Between(x, y, objective.x, objective.y) <= objective.radius);
  }

  draw(scene: Phaser.Scene) {
    scene.add.rectangle(this.bounds.centerX, this.bounds.centerY, this.bounds.width, this.bounds.height, this.palette.base, 1)
      .setStrokeStyle(4, this.palette.border, 1)
      .setDepth(-30);

    const tilesetKey = `tileset_battle_${this.terrainId}`;
    if (scene.textures.exists(tilesetKey)) this.drawRuntimeTiles(scene, tilesetKey);
    else {
      const textureKey = this.runtimeBackgroundKey(scene);
      if (textureKey) scene.add.image(this.bounds.centerX, this.bounds.centerY, textureKey).setDisplaySize(this.bounds.width, this.bounds.height).setAlpha(0.42).setDepth(-29);
    }

    const graphics = scene.add.graphics().setDepth(-20);
    for (const zone of this.zones) {
      graphics.fillStyle(zone.color, zone.alpha);
      graphics.fillRoundedRect(zone.rect.x, zone.rect.y, zone.rect.width, zone.rect.height, 18);
      graphics.lineStyle(2, this.palette.border, 0.26);
      graphics.strokeRoundedRect(zone.rect.x, zone.rect.y, zone.rect.width, zone.rect.height, 18);
      this.drawFeaturePattern(graphics, zone);
      if (zone.label) scene.add.text(zone.rect.centerX, zone.rect.y + 8, zone.label, { fontFamily: 'Georgia, serif', fontSize: '12px', color: '#f3e7c8', stroke: '#111111', strokeThickness: 3 }).setOrigin(0.5, 0).setAlpha(0.72).setDepth(-18);
    }

    for (const objective of this.objectives) {
      scene.add.circle(objective.x, objective.y, objective.radius, 0xd8aa55, 0.08).setStrokeStyle(2, 0xf0cf7a, 0.55).setDepth(-12);
      scene.add.text(objective.x, objective.y - objective.radius - 18, objective.label, { fontFamily: 'Georgia, serif', fontSize: '13px', color: '#f6e6bd', stroke: '#111111', strokeThickness: 3 }).setOrigin(0.5).setDepth(-10);
    }

    scene.add.rectangle(this.attackerDeployment.centerX, this.attackerDeployment.centerY, this.attackerDeployment.width, this.attackerDeployment.height, 0x3d9560, 0.06).setStrokeStyle(2, 0x8fe0ac, 0.25).setDepth(-11);
    scene.add.rectangle(this.defenderDeployment.centerX, this.defenderDeployment.centerY, this.defenderDeployment.width, this.defenderDeployment.height, 0xa94b4b, 0.06).setStrokeStyle(2, 0xe49191, 0.25).setDepth(-11);
  }

  private drawRuntimeTiles(scene: Phaser.Scene, textureKey: string) {
    const columns = Math.ceil(this.bounds.width / 64);
    const rows = Math.ceil(this.bounds.height / 64);
    const seed = this.hash(`${this.provinceId}:${this.terrainId}`);
    for (let row = 0; row < rows; row++) {
      for (let column = 0; column < columns; column++) {
        const frame = Math.abs(seed + column * 17 + row * 31 + column * row * 3) % 32;
        scene.add.image(this.bounds.x + column * 64 + 32, this.bounds.y + row * 64 + 32, textureKey, frame).setDisplaySize(65, 65).setAlpha(0.82).setDepth(-29);
      }
    }
  }

  private hash(value: string) {
    let hash = 2166136261;
    for (let index = 0; index < value.length; index++) {
      hash ^= value.charCodeAt(index);
      hash = Math.imul(hash, 16777619);
    }
    return hash >>> 0;
  }

  private runtimeBackgroundKey(scene: Phaser.Scene) {
    const candidates = [`background_battle_${this.terrainId}`, `battlefield_${this.terrainId}`, this.terrainId === 'coast' ? 'background_colonial_clash' : 'background_javanese_plains'];
    return candidates.find((key) => scene.textures.exists(key));
  }

  private buildZones() {
    const b = this.bounds;
    const add = (id: string, x: number, y: number, width: number, height: number, color: number, alpha: number, modifier: Partial<BattlefieldModifier>, label?: string) => this.zones.push({ id, rect: new Phaser.Geom.Rectangle(x, y, width, height), color, alpha, modifier, label });

    if (this.terrainId === 'rice_fields') {
      add('paddy_north', b.x + b.width * 0.31, b.y + 34, b.width * 0.24, b.height * 0.34, 0x9db967, 0.42, { move: 0.72, defense: 1.05, fatigue: 1.3, cover: 0.08 }, 'Flooded Paddies');
      add('paddy_south', b.x + b.width * 0.48, b.y + b.height * 0.58, b.width * 0.28, b.height * 0.29, 0x86a45f, 0.38, { move: 0.7, defense: 1.08, fatigue: 1.34, cover: 0.08 }, 'Terraced Fields');
      add('causeway', b.x + b.width * 0.44, b.y + 8, b.width * 0.08, b.height - 16, 0xc2a66f, 0.42, { move: 1.16, defense: 0.93 }, 'Raised Causeway');
    } else if (this.terrainId === 'jungle') {
      add('jungle_north', b.x + b.width * 0.22, b.y + 20, b.width * 0.32, b.height * 0.38, 0x244a30, 0.7, { move: 0.64, defense: 1.22, ranged: 0.68, cover: 0.38, fatigue: 1.2 }, 'Dense Jungle');
      add('jungle_south', b.x + b.width * 0.54, b.y + b.height * 0.53, b.width * 0.31, b.height * 0.39, 0x284b32, 0.7, { move: 0.62, defense: 1.25, ranged: 0.65, cover: 0.42, fatigue: 1.22 }, 'Forest Edge');
      add('trail', b.x + 15, b.y + b.height * 0.46, b.width - 30, 74, 0x8e774f, 0.32, { move: 1.18, defense: 0.92, cover: -0.1 }, 'Trade Trail');
    } else if (this.terrainId === 'highlands') {
      add('ridge', b.x + b.width * 0.39, b.y + b.height * 0.16, b.width * 0.25, b.height * 0.58, 0x8a7957, 0.52, { move: 0.72, defense: 1.28, ranged: 1.16, elevation: 2, fatigue: 1.3 }, 'Central Ridge');
      add('slope', b.x + b.width * 0.67, b.y + b.height * 0.35, b.width * 0.18, b.height * 0.44, 0x75694f, 0.4, { move: 0.8, defense: 1.12, elevation: 1, fatigue: 1.18 }, 'Rocky Slope');
    } else if (this.terrainId === 'volcanic') {
      add('ash', b.x + b.width * 0.24, b.y + b.height * 0.2, b.width * 0.52, b.height * 0.55, 0x514844, 0.64, { move: 0.74, ranged: 0.88, fatigue: 1.42, cover: 0.08 }, 'Ash Field');
      add('lava_rock', b.x + b.width * 0.57, b.y + 28, b.width * 0.18, b.height * 0.3, 0x6c4235, 0.5, { move: 0.55, defense: 1.3, cover: 0.2, elevation: 1 }, 'Lava Rock');
    } else if (this.terrainId === 'river_crossing') {
      add('river', b.x + b.width * 0.44, b.y, b.width * 0.12, b.height, 0x34788d, 0.68, { move: 0.38, defense: 0.78, ranged: 0.9, fatigue: 1.5 }, 'River');
      add('ford_north', b.x + b.width * 0.42, b.y + b.height * 0.22, b.width * 0.16, 82, 0x8f9270, 0.54, { move: 0.78, defense: 0.9, fatigue: 1.18 }, 'Northern Ford');
      add('ford_south', b.x + b.width * 0.42, b.y + b.height * 0.68, b.width * 0.16, 82, 0x8f9270, 0.54, { move: 0.78, defense: 0.9, fatigue: 1.18 }, 'Southern Ford');
    } else if (this.terrainId === 'coast') {
      add('shore', b.x + b.width * 0.63, b.y, b.width * 0.37, b.height, 0x3e8294, 0.58, { move: 0.42, defense: 0.75, fatigue: 1.35 }, 'Shallow Coast');
      add('dunes', b.x + b.width * 0.49, b.y + 8, b.width * 0.17, b.height - 16, 0xb29a68, 0.46, { move: 0.78, defense: 1.08, cover: 0.1, fatigue: 1.22 }, 'Dunes');
    } else if (this.terrainId === 'town') {
      add('settlement', b.x + b.width * 0.37, b.y + b.height * 0.18, b.width * 0.38, b.height * 0.62, 0x7c684e, 0.55, { move: 0.72, defense: 1.3, ranged: 0.82, cover: 0.42, fatigue: 1.1 }, 'Town Streets');
      add('market', b.x + b.width * 0.51, b.y + b.height * 0.32, b.width * 0.15, b.height * 0.18, 0xb18a55, 0.52, { move: 0.92, defense: 1.08, cover: 0.15 }, 'Market Square');
      add('walls', b.x + b.width * 0.35, b.y + b.height * 0.13, b.width * 0.43, 42, 0x4d4035, 0.72, { move: 0.46, defense: 1.45, cover: 0.5, elevation: 1 }, 'Town Wall');
    } else {
      add('grove', b.x + b.width * 0.46, b.y + b.height * 0.18, b.width * 0.18, b.height * 0.3, 0x3f673f, 0.5, { move: 0.8, defense: 1.15, ranged: 0.83, cover: 0.24 }, 'Palm Grove');
      add('rise', b.x + b.width * 0.53, b.y + b.height * 0.58, b.width * 0.2, b.height * 0.24, 0x7d7652, 0.36, { move: 0.86, defense: 1.17, ranged: 1.1, elevation: 1 }, 'Low Rise');
    }
  }

  private drawFeaturePattern(graphics: Phaser.GameObjects.Graphics, zone: BattlefieldZone) {
    const { rect } = zone;
    if (zone.id.includes('jungle') || zone.id === 'grove') {
      graphics.fillStyle(0x183b27, 0.5);
      for (let x = rect.x + 22; x < rect.right - 10; x += 42) for (let y = rect.y + 28; y < rect.bottom - 8; y += 48) graphics.fillCircle(x + ((y / 48) % 2) * 12, y, 9);
    } else if (zone.id.includes('paddy')) {
      graphics.lineStyle(2, 0xd6da9a, 0.22);
      for (let y = rect.y + 18; y < rect.bottom; y += 24) graphics.lineBetween(rect.x + 8, y, rect.right - 8, y);
    } else if (zone.id === 'river' || zone.id === 'shore') {
      graphics.lineStyle(2, 0xaadce7, 0.22);
      for (let y = rect.y + 14; y < rect.bottom; y += 30) graphics.lineBetween(rect.x + 8, y, rect.right - 8, y + 7);
    } else if (zone.id === 'settlement') {
      graphics.fillStyle(0x3d3028, 0.42);
      for (let x = rect.x + 26; x < rect.right - 18; x += 56) for (let y = rect.y + 28; y < rect.bottom - 18; y += 52) graphics.fillRect(x, y, 24, 18);
    } else if (zone.id === 'ridge' || zone.id === 'slope' || zone.id === 'lava_rock') {
      graphics.lineStyle(2, 0xe0c792, 0.18);
      for (let y = rect.y + 18; y < rect.bottom; y += 26) graphics.strokeEllipse(rect.centerX, y, rect.width * 0.72, 18);
    }
  }
}
